import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { formatDate, formatDateTime } from '../../../lib/format';
import { EXAM_TYPE_LABEL } from '@dru/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  CursorPage, DashboardAlerts, ExamStatus, ExaminationDetail, ExaminationOrderRow, Partner, Pipeline,
} from '@dru/types';
import { BRAND, EXAM_STATUS_LABEL } from '@dru/types';
import {
  Alert, Button, DataTable, EmptyState, FilterBar, IconAction, RowActions, SearchInput, StatCard, StatSkeleton,
  StatStrip, StatusBadge, cn, type Tone,
} from '@dru/ui';
import {
  CalendarCheck, CalendarClock, CalendarPlus, CircleCheck, ClipboardList, Eye, FlaskConical, Hourglass, Repeat,
  Stethoscope, TriangleAlert, type LucideIcon,
} from 'lucide-react';
import { api } from '../../../lib/api';
import { usePages } from '../../../lib/usePages';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { useToast } from '@dru/ui';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { RescheduleDialog } from '../components/AppointmentActions';
import { ArrangeDialog } from '../components/ArrangeDialog';

/**
 * Medical cases: what is open, where each one has reached, and the one thing
 * HR can do about it.
 *
 * A "medical case" is an examination order. HR reads it as a case — raised,
 * given a lab, booked, attended, reviewed, certified — and the screen uses
 * that word. The state machine decides what is possible; this screen only
 * offers the action the case is actually ready for, and only to somebody who
 * holds the permission the API will check. A button that always 403s or 409s
 * is worse than no button.
 */

/** The state of a case, in the words HR uses. Overrides the shared labels only where they read as internal. */
export const CASE_STATUS_LABEL: Record<ExamStatus, string> = {
  ...EXAM_STATUS_LABEL,
  REQUESTED: 'Awaiting lab assignment',
  ROUTED: 'Lab assigned — to book',
  CONSENT_PENDING: 'Awaiting consent',
  NO_SHOW_OPEN: 'Missed — to rebook',
  LAB_PENDING: 'Awaiting lab results',
};

/** The signed decisions that end a case without a certificate. */
const NOT_CLEARED_LABEL: Record<string, string> = {
  UNFIT: 'Unfit — no certificate',
  TEMPORARILY_UNFIT: 'Temporarily unfit — retest owed',
  FURTHER_EVALUATION: 'Further evaluation required',
};

/**
 * `outcome` is the signed decision, where the row carries one. A case reaches
 * CERTIFIED when the physician signs, whatever they decided, so "Certified"
 * over a worker found unfit said the opposite of what happened.
 */
export function caseStatusTone(s: string, outcome?: string | null): Tone {
  if (s === 'CERTIFIED' && outcome === 'UNFIT') return 'unfit';
  if (s === 'CERTIFIED' && outcome === 'TEMPORARILY_UNFIT') return 'tempUnfit';
  if (s === 'CERTIFIED' && outcome === 'FURTHER_EVALUATION') return 'pending';
  switch (s) {
    case 'CERTIFIED': return 'covered';
    case 'CANCELLED': case 'EXPIRED': case 'NO_SHOW_CLOSED': return 'neutral';
    case 'NO_SHOW_OPEN': case 'RETEST_REQUIRED': return 'overdue';
    case 'REVIEW_PENDING': case 'LAB_PENDING': return 'dueSoon';
    default: return 'pending';
  }
}

export const caseStatusLabel = (s: string, outcome?: string | null) =>
  (s === 'CERTIFIED' && outcome && NOT_CLEARED_LABEL[outcome]) || (CASE_STATUS_LABEL[s as ExamStatus] ?? s);

/** A case nothing more will happen to: its due date is history, not a deadline. */
const TERMINAL: ReadonlySet<string> = new Set(['CERTIFIED', 'CANCELLED', 'EXPIRED', 'NO_SHOW_CLOSED']);

type NextAction = 'route' | 'schedule' | 'rebook' | 'retest';

/**
 * The one action this case is ready for AND this person may take, or null
 * when the move is somebody else's (the lab, the physician, Operations) or
 * the permission the API checks is not held.
 */
export function nextCaseAction(s: string, perms: ReadonlySet<string>): NextAction | null {
  // Choosing the lab is Operations' decision unless this login holds
  // `examination.route`. HR used to be offered "Route and book" here and got a
  // dialog saying there was nothing for them to do.
  if (s === 'REQUESTED') return perms.has('examination.route') && perms.has('appointment.create') ? 'route' : null;
  if (s === 'ROUTED' || s === 'CONSENT_PENDING') return perms.has('appointment.create') ? 'schedule' : null;
  // A no-show is HR's move again: the order is not terminal.
  if (s === 'NO_SHOW_OPEN') return perms.has('appointment.reschedule') ? 'rebook' : null;
  // The physician asked for a repeat: HR raises the linked retest and books it.
  if (s === 'RETEST_REQUIRED') return perms.has('examination.create') && perms.has('appointment.create') ? 'retest' : null;
  return null;
}

export const NEXT_ACTION_LABEL: Record<NextAction, string> = {
  route: 'Assign lab and book',
  schedule: 'Book slot',
  rebook: 'Rebook',
  retest: 'Raise retest',
};

/** The icon for each next action, where it sits in a row's Actions column. */
export const NEXT_ACTION_ICON: Record<NextAction, LucideIcon> = {
  route: CalendarPlus,
  schedule: CalendarPlus,
  rebook: CalendarClock,
  retest: Repeat,
};

const STATUSES: ExamStatus[] = [
  'REQUESTED', 'ROUTED', 'CONSENT_PENDING', 'SCHEDULED', 'NO_SHOW_OPEN', 'CHECKED_IN',
  'IN_PROGRESS', 'LAB_PENDING', 'REVIEW_PENDING', 'RETEST_REQUIRED', 'CERTIFIED',
  'CANCELLED', 'EXPIRED',
];

/**
 * The seven steps every case walks, in HR's words. Shared with the case page
 * so the row's progress bar and the page's timeline say the same thing.
 */
export const CASE_STEPS = [
  { key: 'requested', label: 'Requested', statuses: ['DRAFT', 'REQUESTED', 'ROUTED', 'CONSENT_PENDING'] },
  { key: 'scheduled', label: 'Scheduled', statuses: ['SCHEDULED', 'NO_SHOW_OPEN'] },
  { key: 'checked-in', label: 'Checked in', statuses: ['CHECKED_IN'] },
  { key: 'tests', label: 'Tests', statuses: ['IN_PROGRESS'] },
  { key: 'lab-results', label: 'Lab results', statuses: ['LAB_PENDING'] },
  { key: 'review', label: 'Physician review', statuses: ['REVIEW_PENDING', 'RETEST_REQUIRED'] },
  { key: 'completed', label: 'Completed', statuses: ['CERTIFIED'] },
] as const;

/** Which of CASE_STEPS a status sits at, or -1 for a closed case (cancelled, expired). */
export function caseStep(s: string): number {
  return CASE_STEPS.findIndex((st) => (st.statuses as readonly string[]).includes(s));
}

/** Stuck on HR: missed, or a retest the physician asked for. */
const BLOCKED: ReadonlySet<string> = new Set(['NO_SHOW_OPEN', 'RETEST_REQUIRED']);

/**
 * The row's progress: seven short segments, the current one coloured by how
 * it is going. Read aloud as "Step 4 of 7: Tests".
 */
function CaseProgress({ status }: { status: string }) {
  const at = caseStep(status);
  if (at < 0) return null;
  const blocked = BLOCKED.has(status);
  const done = status === 'CERTIFIED';
  return (
    <span
      role="img"
      aria-label={`Step ${at + 1} of ${CASE_STEPS.length}: ${CASE_STEPS[at]!.label}`}
      title={`Step ${at + 1} of ${CASE_STEPS.length}: ${CASE_STEPS[at]!.label}`}
      className="mt-1.5 flex w-28 gap-0.5"
    >
      {CASE_STEPS.map((st, i) => (
        <span
          key={st.key}
          className={cn(
            'h-1.5 flex-1 rounded-full',
            done ? 'bg-covered-600'
              : i < at ? 'bg-primary-500'
                : i === at ? (blocked ? 'bg-overdue-600' : 'bg-primary-300')
                  : 'bg-neutral-200',
          )}
        />
      ))}
    </span>
  );
}

/** What a case is waiting on when the move is not HR's, in a few words. */
function waitingOn(o: ExaminationOrderRow, canRoute: boolean): string {
  switch (o.status) {
    case 'REQUESTED': return canRoute ? 'Assign a lab' : `${BRAND.name} assigns a lab`;
    case 'ROUTED': case 'CONSENT_PENDING': return 'Slot to be booked';
    case 'SCHEDULED': return 'Employee to attend';
    case 'NO_SHOW_OPEN': return 'Missed — rebook';
    case 'CHECKED_IN': case 'IN_PROGRESS': return 'At the venue';
    case 'LAB_PENDING': return 'Lab results awaited';
    case 'REVIEW_PENDING': return 'Physician reviewing';
    case 'RETEST_REQUIRED': return 'Retest to raise';
    case 'CERTIFIED': return 'Done';
    default: return '—';
  }
}

/** A booked slot, as the appointment list gives it. */
interface Slot { orderId: string; scheduledFor: string; status: string; partner?: { name: string } | null }

export function Examinations() {
  const nav = useNavigate();
  const { permissions } = useAuth();
  // The filter lives in the URL: the dashboard's pipeline and "needs
  // attention" tiles link here with `?status=`, and a filter that only lived
  // in component state meant every one of those links landed on the whole
  // list.
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const overdue = params.get('overdue') === '1';
  const campaignId = params.get('campaignId') ?? '';
  const [search, setSearch] = useState(params.get('q') ?? '');
  const applied = params.get('q') ?? '';
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== applied) {
        setParams((p) => { const n = new URLSearchParams(p); if (search.trim()) n.set('q', search.trim()); else n.delete('q'); return n; }, { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search, applied, setParams]);

  const setFilters = (next: Record<string, string | undefined>) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (next.status) n.set('status', next.status); else n.delete('status');
    if (next.due === 'overdue') n.set('overdue', '1'); else n.delete('overdue');
    return n;
  });

  const [dialog, setDialog] = useState<
    | { kind: 'raise' }
    | { kind: 'route' | 'schedule'; order: ExaminationOrderRow }
    | { kind: 'rebook'; order: ExaminationOrderRow; appointment: { id: string; partnerId: string; scheduledFor: string; partnerName?: string | null } }
    | null
  >(() => (params.get('arrange') === '1' && permissions.has('examination.create') ? { kind: 'raise' } : null));
  // The dashboard's "Schedule checkup" lands here with ?arrange=1. Opened
  // once, then dropped from the address so a reload does not reopen it.
  useEffect(() => {
    if (params.get('arrange') !== '1') return;
    setParams((p) => { const n = new URLSearchParams(p); n.delete('arrange'); return n; }, { replace: true });
  }, [params, setParams]);
  const { toast } = useToast();
  const [opening, setOpening] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const canRead = permissions.has('examination.read');
  const orders = usePages<ExaminationOrderRow>(
    (cursor) => (canRead
      ? api.get<CursorPage<ExaminationOrderRow>>(
        `/examinations?limit=50${status ? `&status=${status}` : ''}${overdue ? '&overdue=true' : ''}${campaignId ? `&campaignId=${campaignId}` : ''}${applied ? `&q=${encodeURIComponent(applied)}` : ''}${cursor ? `&cursor=${cursor}` : ''}`)
      : Promise.resolve({ items: [], nextCursor: null })),
    [status, applied, overdue, campaignId, canRead],
  );

  // The counts above the list come from the server's aggregates, never from
  // the loaded page: a paged list counted in the browser says 50 when the
  // company has 4,000. Each is fetched only for a role that may read it.
  const pipeline = useQuery<Pipeline | null>(
    () => (canRead ? api.get<Pipeline>(`/dashboard/pipeline${campaignId ? `?campaignId=${campaignId}` : ''}`) : Promise.resolve(null)),
    [canRead, campaignId],
  );
  const canSeeAlerts = permissions.has('compliance.read');
  const alerts = useQuery<DashboardAlerts | null>(
    () => (canSeeAlerts ? api.get<DashboardAlerts>('/dashboard/alerts') : Promise.resolve(null)),
    [canSeeAlerts],
  );
  // The alerts are company-wide; the pipeline above follows ?campaignId=.
  // Mixing the two gave a campaign's tiles the whole company's retests, so a
  // campaign view counts from its own pipeline only.
  const a = campaignId ? null : alerts.data;
  const p = pipeline.data;

  // The booked slot and the lab, for the rows on screen. The case list does
  // not carry either, so the bookings ahead and the missed ones are read once
  // and matched by case; a lab's name is looked up once per lab.
  const canReadSlots = permissions.has('appointment.read');
  const slots = useQuery<Map<string, Slot>>(async () => {
    const byOrder = new Map<string, Slot>();
    if (!canReadSlots) return byOrder;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const [ahead, missed] = await Promise.all([
      api.get<{ items: Slot[] }>(`/appointments?from=${today.toISOString()}&limit=100`),
      api.get<{ items: Slot[] }>('/appointments?attention=1&limit=100'),
    ]);
    for (const x of [...missed.items, ...ahead.items]) if (!byOrder.has(x.orderId)) byOrder.set(x.orderId, x);
    return byOrder;
  }, [canReadSlots]);
  const readsNetwork = permissions.has('partner.read');
  const partnerIds = useMemo(
    () => [...new Set(orders.items.map((o) => o.assignedPartnerId).filter((x): x is string => Boolean(x)))].sort().join(','),
    [orders.items],
  );
  const [labNames, setLabNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!readsNetwork || !partnerIds) return;
    const missing = partnerIds.split(',').filter((id) => !(id in labNames));
    if (!missing.length) return;
    let live = true;
    void Promise.all(missing.map((id) => api.get<Partner>(`/partners/${id}`).then((x) => [id, x.name] as const).catch(() => [id, ''] as const)))
      .then((pairs) => { if (live) setLabNames((m) => ({ ...m, ...Object.fromEntries(pairs) })); });
    return () => { live = false; };
    // labNames is read, not watched: it only grows, and watching it would refetch nothing new.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerIds, readsNetwork]);

  const unfiltered = !status && !overdue && !applied && !campaignId;

  // The retest is a new order, routed like the old one; it then needs a slot.
  async function raiseRetest(o: ExaminationOrderRow) {
    setOpening(o.id); setRowError(null);
    try {
      const next = await api.post<ExaminationOrderRow>(`/examinations/${o.id}/retest`, {}, crypto.randomUUID());
      setDialog({ kind: 'schedule', order: { ...next, employee: o.employee } });
    } catch (e) { setRowError(errorMessage(e)); } finally { setOpening(null); }
  }

  // Rebooking needs the missed appointment, which the row does not carry.
  async function openRebook(o: ExaminationOrderRow) {
    setOpening(o.id); setRowError(null);
    try {
      const d = await api.get<ExaminationDetail>(`/examinations/${o.id}`);
      const missed = (d.appointments ?? []).find((x) => x.status === 'NO_SHOW');
      if (!missed) throw new Error('No missed appointment is on this case to rebook from.');
      setDialog({ kind: 'rebook', order: o, appointment: { id: missed.id, partnerId: missed.partnerId, scheduledFor: missed.scheduledFor, partnerName: missed.partner?.name } });
    } catch (e) { setRowError(errorMessage(e)); } finally { setOpening(null); }
  }

  function act(o: ExaminationOrderRow, action: NextAction) {
    if (action === 'retest') void raiseRetest(o);
    else if (action === 'rebook') void openRebook(o);
    else setDialog({ kind: action, order: o });
  }

  const canArrange = permissions.has('examination.create');
  const filtered = Boolean(status || applied || overdue);

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Medical cases" />
        <EmptyState title="Medical cases are not part of your role" hint="Ask your company administrator if you need to follow employees' medical cases." />
      </div>
    );
  }

  const stage = (k: string) => p?.stages.find((st) => st.key === k)?.count ?? 0;
  const retests = a?.retestsRequired ?? 0;
  const missed = p?.stalled ?? 0;
  const show = (next: string | null) => setParams((prev) => {
    const n = new URLSearchParams(prev);
    n.delete('overdue');
    if (next) n.set('status', next); else n.delete('status');
    return n;
  });
  const canRoute = permissions.has('examination.route');
  const lateOf = (o: ExaminationOrderRow) => !TERMINAL.has(o.status) && Boolean(o.dueDate && new Date(o.dueDate) < new Date());

  return (
    <div className="space-y-5">
      <PageHeader
        title="Medical cases"
        hint={`Every medical checkup raised for your workforce, where it has reached, and what it is waiting for. The tests, the lab network and the physician are arranged by ${BRAND.name}.`}
        actions={canArrange && (
          <Button icon={<CalendarPlus className="h-4 w-4" />} onClick={() => setDialog({ kind: 'raise' })}>Schedule checkup</Button>
        )}
      />

      {/* ── Where every case stands ───────────────────────── */}
      {pipeline.loading && !p ? <StatSkeleton count={7} /> : p && (
        <StatStrip className="grid-cols-2 md:grid-cols-4 xl:grid-cols-7">
          <StatCard label="Total" value={p.total} icon={<ClipboardList className="h-6 w-6" />}
                    sub={`${p.open} open · ${p.closed} closed`}
                    className={cn(unfiltered && 'ring-2 ring-primary-500')}
                    onClick={() => setParams(new URLSearchParams(), { replace: false })} />
          <StatCard label="Scheduled" value={stage('SCHEDULED')} icon={<CalendarCheck className="h-6 w-6" />}
                    sub="Slot booked"
                    className={cn(status === 'SCHEDULED' && 'ring-2 ring-primary-500')}
                    onClick={() => show('SCHEDULED')} />
          {/* Two states, and the list filters by one: the tile names both
              instead of opening half of them. */}
          <StatCard label="In progress" value={stage('CHECKED_IN') + stage('IN_PROGRESS')}
                    valueTone={stage('CHECKED_IN') + stage('IN_PROGRESS') ? 'pending' : undefined}
                    icon={<Hourglass className="h-6 w-6" />}
                    sub={<span className="flex flex-wrap gap-x-2">
                      <TileLink active={status === 'CHECKED_IN'} onClick={() => show('CHECKED_IN')}>{stage('CHECKED_IN')} checked in</TileLink>
                      <TileLink active={status === 'IN_PROGRESS'} onClick={() => show('IN_PROGRESS')}>{stage('IN_PROGRESS')} at tests</TileLink>
                    </span>} />
          <StatCard label="Awaiting results" value={stage('LAB_PENDING')} icon={<FlaskConical className="h-6 w-6" />}
                    sub="With the lab"
                    className={cn(status === 'LAB_PENDING' && 'ring-2 ring-primary-500')}
                    onClick={() => show('LAB_PENDING')} />
          {/* The server's review stage includes the retests; they are counted
              under Needs attention instead, when this login can see that count. */}
          <StatCard label="Pending review" value={Math.max(0, stage('REVIEW_PENDING') - retests)}
                    valueTone={stage('REVIEW_PENDING') - retests > 0 ? 'dueSoon' : undefined}
                    icon={<Stethoscope className="h-6 w-6" />}
                    sub={a ? 'With the physician' : 'With the physician, incl. retests'}
                    className={cn(status === 'REVIEW_PENDING' && 'ring-2 ring-primary-500')}
                    onClick={() => show('REVIEW_PENDING')} />
          <StatCard label="Completed" value={p.certified} valueTone={p.certified ? 'covered' : undefined}
                    icon={<CircleCheck className="h-6 w-6" />}
                    sub="Physician signed off"
                    className={cn(status === 'CERTIFIED' && 'ring-2 ring-primary-500')}
                    onClick={() => show('CERTIFIED')} />
          <StatCard label="Needs attention" value={missed + retests}
                    valueTone={missed + retests ? 'overdue' : undefined}
                    icon={<TriangleAlert className="h-6 w-6" />}
                    sub={<span className="flex flex-wrap gap-x-2">
                      <TileLink active={status === 'NO_SHOW_OPEN'} onClick={() => show('NO_SHOW_OPEN')}>{missed} missed</TileLink>
                      {a && <TileLink active={status === 'RETEST_REQUIRED'} onClick={() => show('RETEST_REQUIRED')}>{retests} retest{retests === 1 ? '' : 's'}</TileLink>}
                      {a && a.overdueExaminations > 0 && (
                        <TileLink active={overdue} onClick={() => setFilters({ due: 'overdue' })}>{a.overdueExaminations} past due</TileLink>
                      )}
                    </span>} />
        </StatStrip>
      )}
      {pipeline.error && <Alert tone="danger">Counts could not be loaded: {pipeline.error}</Alert>}

      <FilterBar
        filters={[
          {
            key: 'status', label: 'Status', allLabel: 'All statuses',
            options: STATUSES.map((s) => ({ value: s, label: CASE_STATUS_LABEL[s] })),
          },
          {
            key: 'due', label: 'Due', allLabel: 'Any due date',
            options: [{ value: 'overdue', label: 'Past due date' }],
          },
        ]}
        value={{ status: status || undefined, due: overdue ? 'overdue' : undefined }}
        onChange={setFilters}
      >
        <SearchInput
          value={search}
          onValueChange={setSearch}
          aria-label="Search by employee name or employee ID"
          placeholder="Search name or employee ID"
          className="w-full sm:w-72"
        />
      </FilterBar>

      {rowError && <Alert tone="danger">{rowError}</Alert>}

      <DataTable<ExaminationOrderRow>
        rows={orders.items}
        loading={orders.loading}
        error={orders.error}
        onRetry={orders.reload}
        hasMore={orders.hasMore}
        onLoadMore={orders.loadMore}
        loadingMore={orders.loadingMore}
        rowKey={(o) => o.id}
        onRowClick={(o) => nav(`/hr/examinations/${o.id}`)}
        empty={{
          title: applied ? 'No medical case matches that search' : filtered ? 'No medical cases match these filters' : 'No medical cases yet',
          hint: applied
            ? 'Try a different name or employee ID.'
            : filtered
              ? 'Clear a filter to see more.'
              : 'Schedule a checkup for an employee to get started.',
          action: filtered || !canArrange ? undefined : (
            <Button onClick={() => setDialog({ kind: 'raise' })}>Schedule checkup</Button>
          ),
        }}
        columns={[
          {
            key: 'code', header: 'Employee ID', hideOnMobile: true,
            cell: (o) => <span className="whitespace-nowrap font-mono text-small text-neutral-600">{o.employee?.employeeCode ?? '—'}</span>,
          },
          {
            key: 'employee', header: 'Name', mobileLabel: false,
            // Straight off the row. The endpoint joins the worker server-side.
            cell: (o) => (o.employee ? (
              <div className="min-w-0">
                <div className="font-medium text-neutral-900">{o.employee.fullName}</div>
                <div className="font-mono text-caption text-neutral-500 md:hidden">{o.employee.employeeCode}</div>
              </div>
            ) : <span className="font-mono text-caption text-neutral-500">{o.employeeId.slice(0, 8)}…</span>),
          },
          {
            key: 'type', header: 'Checkup type',
            cell: (o) => (
              <span className="text-small text-neutral-700">
                {(EXAM_TYPE_LABEL[o.examinationType] ?? o.examinationType)}
              </span>
            ),
          },
          {
            key: 'lab', header: 'Lab',
            cell: (o) => {
              const name = (o.assignedPartnerId && labNames[o.assignedPartnerId]) || slots.data?.get(o.id)?.partner?.name;
              if (name) return <span className="text-small text-neutral-700">{name}</span>;
              return <span className="text-small text-neutral-400">{o.assignedPartnerId ? 'Assigned' : 'Not yet assigned'}</span>;
            },
          },
          {
            key: 'date', header: 'Date',
            cell: (o) => {
              // The booking when there is one; otherwise the date the case is
              // due by. Blank once the case is over: a due date beside
              // "Completed" reads as something still owed.
              const slot = slots.data?.get(o.id);
              if (slot && !TERMINAL.has(o.status)) {
                return (
                  <div className="whitespace-nowrap">
                    <div className={cn('text-small tabular-nums', o.status === 'NO_SHOW_OPEN' ? 'text-overdue-600' : 'text-neutral-900')}>
                      {formatDateTime(slot.scheduledFor)}
                    </div>
                    <div className="text-caption text-neutral-500">{o.status === 'NO_SHOW_OPEN' ? 'Missed slot' : 'Booked'}</div>
                  </div>
                );
              }
              if (TERMINAL.has(o.status) || !o.dueDate) return <span className="text-small text-neutral-400">—</span>;
              const late = lateOf(o);
              return (
                <div className="whitespace-nowrap">
                  <div className={cn('text-small tabular-nums', late ? 'font-medium text-overdue-600' : 'text-neutral-700')}>{formatDate(o.dueDate)}</div>
                  <div className={cn('text-caption', late ? 'text-overdue-600' : 'text-neutral-500')}>{late ? 'Past due' : 'Due by'}</div>
                </div>
              );
            },
          },
          {
            key: 'status', header: 'Status', mobileLabel: false,
            cell: (o) => (
              <div>
                <div className="flex flex-wrap items-center gap-1">
                  <StatusBadge tone={caseStatusTone(o.status, o.outcome)}>{caseStatusLabel(o.status, o.outcome)}</StatusBadge>
                  {lateOf(o) && !BLOCKED.has(o.status) && <StatusBadge tone="overdue">Delayed</StatusBadge>}
                </div>
                <CaseProgress status={o.status} />
              </div>
            ),
          },
          {
            key: 'action', header: 'Next action', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (o) => {
              const action = nextCaseAction(o.status, permissions);
              const who = o.employee?.fullName ?? 'this case';
              const Icon = action ? NEXT_ACTION_ICON[action] : null;
              return (
                <div className="flex items-center justify-end gap-2">
                  {action && Icon ? (
                    <Button
                      size="sm"
                      variant={BLOCKED.has(o.status) ? 'primary' : 'secondary'}
                      icon={<Icon className="h-4 w-4" />}
                      loading={opening === o.id}
                      aria-label={`${NEXT_ACTION_LABEL[action]} for ${who}`}
                      onClick={(e) => { e.stopPropagation(); act(o, action); }}
                    >
                      {NEXT_ACTION_LABEL[action]}
                    </Button>
                  ) : (
                    <span className="whitespace-nowrap text-small text-neutral-500">{waitingOn(o, canRoute)}</span>
                  )}
                  <RowActions>
                    <IconAction label={`View details of the medical case for ${who}`} icon={Eye} onClick={() => nav(`/hr/examinations/${o.id}`)} />
                  </RowActions>
                </div>
              );
            },
          },
        ]}
      />

      {dialog?.kind === 'raise' && (
        <ArrangeDialog
          onClose={() => { setDialog(null); orders.reload(); }}
          onDone={() => { setDialog(null); orders.reload(); toast('success', 'Medical case arranged.'); }}
        />
      )}
      {(dialog?.kind === 'route' || dialog?.kind === 'schedule') && (
        <ArrangeDialog
          order={dialog.order}
          onClose={() => { setDialog(null); orders.reload(); }}
          onDone={() => { setDialog(null); orders.reload(); }}
        />
      )}
      {dialog?.kind === 'rebook' && (
        <RescheduleDialog
          appointmentId={dialog.appointment.id}
          partnerId={dialog.appointment.partnerId}
          partnerName={dialog.appointment.partnerName}
          who={dialog.order.employee?.fullName}
          scheduledFor={dialog.appointment.scheduledFor}
          mode="rebook"
          onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); orders.reload(); toast('success', 'Rebooked.'); }}
        />
      )}
    </div>
  );
}

/** A count inside a summary tile that opens the list filtered to it. */
function TileLink({ active, onClick, children }: { active?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded text-left underline-offset-2 hover:text-primary-700 hover:underline max-md:min-h-8',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        active ? 'font-semibold text-primary-700 underline' : 'text-neutral-600',
      )}
    >
      {children}
    </button>
  );
}
